import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountantService } from 'src/app/services/accountatnt/accountant.service';
import { CertificateA1 } from 'src/app/models/NfceWizard/CertificateA1';
import { CompanyNfceInformation } from "src/app/models/NfceWizard/CompanyNfceInformation";
import { CompanyNfce } from 'src/app/models/NfceWizard/Company';
import { SessionService } from 'src/app/services/session/session.service';
import { WizardNfceService } from 'src/app/services/nfce/wizard/wizard-nfce.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { PdvNfceInformation } from 'src/app/models/NfceWizard/PdvNfceInformation';
import { PdvService } from 'src/app/services/pdv/pdv.service';
import { PdvNfce } from 'src/app/models/Pdv/PdvNfce.model';

export interface Client {
  companyName: string;
  fantasyName: string;
  cnpj: string;
  phone: string;
  dialCode: string;
  stateRegistration: string;
  cep: string;
  address: string;
  district: string;
  number: string;
  complement: string;
  city: string;
  state: string;
  ibge: string;
}

export interface Certificate {
  serialNumber: string;
  friendlyName: string;
  dateValidFrom: string;
  dateValidUntil: string;
  type: string;
  password: string;
}

export interface NfceInformation {
  cscId: number;
  csc: string;
  environment: number;
}

@Component({
  selector: 'app-accountant-authentication',
  templateUrl: './accountant-authentication.component.html',
  styleUrls: ['./accountant-authentication.component.scss']
})
export class AccountantAuthenticationComponent implements OnInit {
  guid: string = '';
  isLoading: boolean = true;
  isValid: boolean = false;
  isSaving: boolean = false;
  errorMessage: string = '';

  clientName: string = '';
  clientEmail: string = '';
  clientDocument: string = '';
  client: Client | null = null;
  nfceInformation: NfceInformation | null = null;
  certificateForm: FormGroup;
  selectedFile: File | null = null;
  nfceForm: FormGroup;
  isSavingNfce: boolean = false;
  companyForm: FormGroup;
  isSavingCompany: boolean = false;
  pdvList: PdvNfce[] = [];
  pdvPagedList: PdvNfce[] = [];
  pdvPageSize: number = 10;
  pdvPageIndex: number = 0;
  isSavingPdv: boolean = false;
  editingPdvId: number | null = null;
  editingPdvNumeration: number = 0;
  newPdvSerie: number | null = null;
  newPdvNumeration: number | null = null;
  private pdvRequestId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private accountantService: AccountantService,
    private sessionService: SessionService,
    private wizardNfceService: WizardNfceService,
    private pdvService: PdvService,
    private configuration: ConfigurationService
  ) {
    this.certificateForm = this.fb.group({
      serialNumber: [''],
      friendlyName: [''],
      dateValidFrom: [''],
      dateValidUntil: [''],
      type: [''],
      password: ['']
    });
    this.nfceForm = this.fb.group({
      cscId: [0],
      csc: [''],
      environment: [1]
    });
    this.companyForm = new FormGroup({
      cnpj: new FormControl('', Validators.required),
      stateRegistration: new FormControl('', Validators.required),
      address: new FormGroup({
        cep: new FormControl('', Validators.required),
        address: new FormControl('', Validators.required),
        district: new FormControl('', Validators.required),
        number: new FormControl('', Validators.required),
        complement: new FormControl(''),
        city: new FormControl('', Validators.required),
        state: new FormControl('', Validators.required),
        ibge: new FormControl('')
      }),
      companyName: new FormControl('', [Validators.required, Validators.minLength(3)]),
      fantasyName: new FormControl('', [Validators.required, Validators.minLength(3)]),
      phone: new FormControl('', Validators.required),
      dialCode: new FormControl('')
    });
  }

  ngOnInit(): void {
    const nav = this.router.getCurrentNavigation();
    const initialState = nav?.extras?.state as any;

    this.route.queryParams.subscribe(params => {
      const newGuid = params['guid'];
      if (!newGuid) {
        this.errorMessage = 'Link inválido. GUID não encontrado.';
        this.isLoading = false;
        return;
      }

      const guidChanged = newGuid !== this.guid;
      this.guid = newGuid;

      if (initialState?.accountant && !guidChanged) {
        this.loadClientData(initialState.accountant);
        this.isLoading = false;
      } else {
        this.isLoading = true;
        this.validateGuid();
      }
    });
  }


  private loadClientData(data: any): void {
    // Reseta todos os dados antes de carregar o novo cliente
    this.client = null;
    this.nfceInformation = null;
    this.pdvList = [];
    this.pdvPagedList = [];
    this.pdvPageIndex = 0;
    this.editingPdvId = null;
    this.selectedFile = null;
    this.certificateForm.reset();
    this.nfceForm.reset({ cscId: 0, csc: '', environment: 2 });

    this.isValid = true;
    this.clientName = data.name;
    this.clientEmail = data.email;
    this.clientDocument = data.document;
    this.client = data.client || null;
    this.nfceInformation = data.nfceInformation || null;

    if (data.client) {
      const c = data.client as Client;
      this.companyForm.patchValue({
        cnpj: c.cnpj,
        stateRegistration: c.stateRegistration,
        address: {
          cep: c.cep,
          address: c.address,
          district: c.district,
          number: c.number,
          complement: c.complement,
          city: c.city,
          state: c.state,
          ibge: c.ibge
        },
        companyName: c.companyName,
        fantasyName: c.fantasyName,
        phone: c.dialCode ? '+' + c.dialCode + c.phone : c.phone,
        dialCode: c.dialCode
      });
    }

    if (data.certificate) {
      this.certificateForm.patchValue({
        serialNumber: data.certificate.serialNumber || '',
        friendlyName: data.certificate.friendlyName || '',
        dateValidFrom: data.certificate.dateValidFrom || '',
        dateValidUntil: data.certificate.dateValidUntil || '',
        type: data.certificate.type || ''
      });
    }

    if (data.nfceInformation) {
      this.nfceForm.patchValue({
        cscId: data.nfceInformation.cscId || 0,
        csc: data.nfceInformation.csc || '',
        environment: data.nfceInformation.environment || 2
      });
    }

    const requestId = ++this.pdvRequestId;
    this.pdvService.getNfcePdv().subscribe(pdvs => {
      if (requestId !== this.pdvRequestId) return;
      this.pdvList = pdvs || [];
      this.updatePdvPage();
    });
  }

  async validateGuid(): Promise<void> {
    try {
      const response = await this.accountantService.validateAccountantGuid(this.guid);

      if (response.success) {
        if (response.data.token) {
          this.sessionService.setToken({
            token: response.data.token,
            refreshToken: response.data.refreshToken || ''
          });
        }
        
        this.loadClientData(response.data);
      } else {
        this.errorMessage = response.message || 'Link inválido ou expirado.';
        this.isValid = false;
      }
    } catch (error: any) {
      this.errorMessage = error.message || 'Erro ao validar o link. Tente novamente.';
      this.isValid = false;
      this.snackBar.open(this.errorMessage, '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isLoading = false;
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar extensão do arquivo
      const extension = file.name.split('.').pop()?.toLowerCase();
      const validExtensions = ['p12', 'pfx'];
      
      if (!validExtensions.includes(extension || '')) {
        this.snackBar.open('Formato do certificado inválido. Use arquivos .p12 ou .pfx', '', {
          duration: 5000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }
      
      this.selectedFile = file;
    }
  }

  async onSaveCertificate(): Promise<void> {
    this.isSaving = true;
    try {
      if (!this.selectedFile) {
        this.snackBar.open('Por favor, selecione um arquivo de certificado.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }

      const password = this.certificateForm.get('password')?.value;
      if (!password) {
        this.snackBar.open('Por favor, informe a senha do certificado.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }

      const file: CertificateA1 = {
        file: this.selectedFile,
        password: password
      };

      const response = await this.accountantService.sendCertificate(file);
      
      if (response) {
        this.snackBar.open('Certificado atualizado com sucesso!', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });

        this.certificateForm.patchValue({
          serialNumber: this.certificateForm.get('serialNumber')?.value || 'uploaded',
        });

        location.reload();

        this.selectedFile = null;
        this.certificateForm.patchValue({ password: '' });
      } else {
        this.snackBar.open('Erro ao salvar certificado.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
      }
    } catch (error: any) {
      this.snackBar.open(error.message || 'Erro ao salvar certificado.', '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isSaving = false;
    }
  }

  updatePdvPage(): void {
    const start = this.pdvPageIndex * this.pdvPageSize;
    this.pdvPagedList = this.pdvList.slice(start, start + this.pdvPageSize);
  }

  onPdvPageChange(event: any): void {
    this.pdvPageIndex = event.pageIndex;
    this.pdvPageSize = event.pageSize;
    this.editingPdvId = null;
    this.updatePdvPage();
  }

  async onSaveNewPdv(): Promise<void> {
    if (this.newPdvSerie == null || this.newPdvNumeration == null) {
      this.snackBar.open('Por favor, preencha a série e a numeração.', '', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['warning-snackbar']
      });
      return;
    }
    this.isSavingPdv = true;
    try {
      const pdvInfo: PdvNfceInformation = { serie: this.newPdvSerie, numerationNfce: this.newPdvNumeration - 1 };
      await this.wizardNfceService.SetCompanyNfceInformationOutOfWizard(pdvInfo);
      this.snackBar.open('Série cadastrada com sucesso!', '', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['green-snackbar']
      });
      this.newPdvSerie = null;
      this.newPdvNumeration = null;
      const requestId = ++this.pdvRequestId;
      this.pdvService.getNfcePdv().subscribe(pdvs => {
        if (requestId !== this.pdvRequestId) return;
        this.pdvList = pdvs || [];
        this.updatePdvPage();
      });
    } catch (error: any) {
      this.snackBar.open(error.message || 'Erro ao cadastrar série.', '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isSavingPdv = false;
    }
  }

  async onSavePdvInformation(pdv: PdvNfce): Promise<void> {
    this.isSavingPdv = true;
    try {
      if (pdv.serie == null || this.editingPdvNumeration == null) {
        this.snackBar.open('Por favor, preencha a série e a numeração.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }
      const pdvInfo: PdvNfceInformation = { serie: pdv.serie, numerationNfce: this.editingPdvNumeration - 1, pdvId: pdv.id };
      await this.wizardNfceService.SetCompanyNfceInformationOutOfWizard(pdvInfo);
      pdv.numeration = this.editingPdvNumeration - 1;
      this.editingPdvId = null;
      this.snackBar.open('Série do PDV atualizada com sucesso!', '', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['green-snackbar']
      });
    } catch (error: any) {
      this.snackBar.open(error.message || 'Erro ao salvar série do PDV.', '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isSavingPdv = false;
    }
  }

  dialCodeChange(dialCode: string): void {
    this.companyForm.get('dialCode')?.setValue(dialCode);
  }

  async onSaveCompanyData(): Promise<void> {
    this.companyForm.markAllAsTouched();
    if (!this.companyForm.valid) return;

    this.isSavingCompany = true;
    try {
      const raw = this.companyForm.getRawValue();
      const company: CompanyNfce = {
        cnpj: raw.cnpj,
        stateRegistration: raw.stateRegistration,
        address: {
          address: raw.address.address,
          cep: raw.address.cep,
          city: raw.address.city,
          complement: raw.address.complement,
          district: raw.address.district,
          state: raw.address.state,
          number: raw.address.number,
          ibge: raw.address.ibge
        },
        companyName: raw.companyName,
        fantasyName: raw.fantasyName,
        phone: this.configuration.returnOnlyPhoneNumberWithoutDialCode(raw.dialCode, raw.phone),
        dialCode: raw.dialCode
      };

      const saved = await this.wizardNfceService.setCompanyDetails(company);
      if (saved) {
        this.snackBar.open('Dados da empresa atualizados com sucesso!', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
      } else {
        this.snackBar.open('Erro ao salvar dados da empresa.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
      }
    } catch (error: any) {
      this.snackBar.open(error.message || 'Erro ao salvar dados da empresa.', '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isSavingCompany = false;
    }
  }

  async onSaveNfceInformation(): Promise<void> {
    this.isSavingNfce = true;
    try {
      const nfceData = this.nfceForm.value;
      
      if (!nfceData.csc || !nfceData.cscId) {
        this.snackBar.open('Por favor, preencha o CSC e CSC ID.', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }
      
      const companyInformation: CompanyNfceInformation = {
        cscId: nfceData.cscId,
        csc: nfceData.csc,
        environment: nfceData.environment
      }

      await this.accountantService.updateNfceInformation(companyInformation);
      
      this.snackBar.open('Informações NFCe atualizadas com sucesso!', '', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['green-snackbar']
      });
      
      // Recarrega os dados da página
      await this.validateGuid();

    } catch (error: any) {
      this.snackBar.open(error.message || 'Erro ao salvar informações NFCe.', '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    } finally {
      this.isSavingNfce = false;
    }
  }
}
